/**
 * V55.7: 직원 프로필 사진 섹션 (Cloud Function 경유)
 *
 * 직원 상세 화면 좌측 상단에 표시되는 프로필 사진 컴포넌트입니다.
 *
 * **V55.7 변경사항:**
 * - Storage 직접 업로드 → Cloud Function 경유로 변경
 * - 보안: 서버에서 HR/Admin 권한 검증
 * - Storage Rules: 클라이언트 직접 write 불가
 *
 * 규격:
 * - 3x4 비율 (반명함 사진 비율)
 * - object-fit: cover (찌그러짐 없이 중앙 crop)
 *
 * 기능:
 * - 사진 표시 (placeholder 또는 실제 이미지)
 * - 사진 추가 (Cloud Function 업로드)
 * - 사진 삭제 (Cloud Function 삭제 + 확인 팝업)
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Camera, Trash2, Loader, User, AlertCircle } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../../../../firebase/config';

interface EmployeePhotoSectionProps {
  /** 직원 ID */
  employeeId: string;
  /**
   * V55.11: Storage 경로 (employee-photos/{employeeId})
   * - getDownloadURL()로 인증된 URL 획득
   */
  photoPath?: string;
  /** 직원 이름 (alt용) */
  employeeName: string;
  /** 수정 권한 여부 */
  canEdit: boolean;
  /** 새로고침 콜백 */
  onRefresh: () => void;
}

/**
 * V55.10: 캐시 무효화용 쿼리스트링 추가 (안정화)
 * - cacheKey를 인자로 받아 렌더마다 URL이 바뀌지 않도록 함
 * - 업로드/삭제/props 변경 시에만 cacheKey 갱신
 */
function buildDisplayURL(url: string | undefined, cacheKey: number): string | undefined {
  if (!url) return undefined;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${cacheKey}`;
}

// 사진 영역 크기 (3:4 비율)
const PHOTO_WIDTH = 120;
const PHOTO_HEIGHT = 160; // 3:4 = 120:160

// 파일 크기 제한: 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export function EmployeePhotoSection({
  employeeId,
  photoPath,
  employeeName,
  canEdit,
  onRefresh,
}: EmployeePhotoSectionProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // V55.11: Storage 경로 기반 로컬 상태
  // - photoPath가 있으면 getDownloadURL()로 URL 획득
  // - 업로드 성공 시 즉시 localPhotoPath 업데이트 → useEffect에서 URL 획득
  const [localPhotoPath, setLocalPhotoPath] = useState<string | undefined>(photoPath);
  const [resolvedURL, setResolvedURL] = useState<string | undefined>(undefined);
  const [isLoadingURL, setIsLoadingURL] = useState<boolean>(false);
  const [cacheKey, setCacheKey] = useState<number>(Date.now());
  const [imageError, setImageError] = useState<boolean>(false);

  // V55.11: photoPath → getDownloadURL() → resolvedURL
  useEffect(() => {
    if (!localPhotoPath || !storage) {
      setResolvedURL(undefined);
      return;
    }

    setIsLoadingURL(true);
    setImageError(false);

    const storageRef = ref(storage, localPhotoPath);
    getDownloadURL(storageRef)
      .then((url) => {
        setResolvedURL(url);
        setIsLoadingURL(false);
      })
      .catch((error) => {
        console.error('[EmployeePhotoSection] getDownloadURL 실패:', error instanceof Error ? error.message : String(error));
        setResolvedURL(undefined);
        setIsLoadingURL(false);
        setImageError(true);
      });
  }, [localPhotoPath, cacheKey]);

  // props.photoPath가 변경되면 로컬 상태 동기화
  useEffect(() => {
    setLocalPhotoPath(photoPath);
    setCacheKey(Date.now()); // props 변경 시 캐시 무효화
    setImageError(false); // 에러 상태 초기화
  }, [photoPath]);

  // V55.11: 표시할 URL (getDownloadURL로 획득한 resolvedURL 사용)
  // - 캐시 무효화는 cacheKey 변경 → useEffect 재실행 → 새 URL 획득
  const displayPhotoURL = useMemo(
    () => buildDisplayURL(resolvedURL, cacheKey),
    [resolvedURL, cacheKey]
  );

  // V55.11: 이미지 로딩 실패 핸들러
  const handleImageError = () => {
    console.error('[EmployeePhotoSection] 이미지 로딩 실패');
    setImageError(true);
  };

  // V55.11: 이미지 로딩 성공 핸들러
  const handleImageLoad = () => {
    setImageError(false);
  };

  // 파일 선택 트리거
  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  // 파일을 Base64로 변환
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // data:image/jpeg;base64,xxxxx 형식에서 base64 부분만 추출
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // 파일 업로드 처리 (Cloud Function 호출)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 이미지 파일만 허용
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    // 파일 크기 제한 (5MB)
    if (file.size > MAX_FILE_SIZE) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    // Firebase Functions 인스턴스 확인
    if (!functions) {
      alert('Firebase가 초기화되지 않았습니다. 관리자에게 문의하세요.');
      return;
    }

    setIsUploading(true);

    try {
      // 파일을 Base64로 변환
      const fileBase64 = await fileToBase64(file);

      // V55.11: Cloud Function 호출 (photoPath 반환)
      const uploadEmployeePhoto = httpsCallable<
        { employeeId: string; fileBase64: string; contentType: string; fileName: string },
        { success: boolean; photoPath: string }
      >(functions, 'uploadEmployeePhoto');

      const result = await uploadEmployeePhoto({
        employeeId,
        fileBase64,
        contentType: file.type,
        fileName: file.name,
      });

      if (result.data.success) {
        // V55.11: 즉시 UI 반영 - 반환된 photoPath로 로컬 상태 업데이트
        // → useEffect에서 getDownloadURL()로 URL 획득
        setLocalPhotoPath(result.data.photoPath);
        setCacheKey(Date.now()); // 캐시 무효화 (새 URL 획득 트리거)
        setImageError(false); // 에러 상태 초기화
        alert('사진이 업로드되었습니다.');
        onRefresh(); // 서버 데이터 동기화
      } else {
        throw new Error('업로드 실패');
      }
    } catch (error: any) {
      console.error('[EmployeePhotoSection] 업로드 실패:', error instanceof Error ? error.message : String(error));

      // Firebase Functions 에러 메시지 추출
      const errorMessage = error.message || '사진 업로드에 실패했습니다.';
      alert(errorMessage);
    } finally {
      setIsUploading(false);
      // 파일 입력 초기화 (같은 파일 재선택 가능하도록)
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 삭제 확인
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  // 삭제 취소
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  // 삭제 실행 (Cloud Function 호출)
  const handleDeleteConfirm = async () => {
    // Firebase Functions 인스턴스 확인
    if (!functions) {
      alert('Firebase가 초기화되지 않았습니다. 관리자에게 문의하세요.');
      setShowDeleteConfirm(false);
      return;
    }

    setIsDeleting(true);
    setShowDeleteConfirm(false);

    try {
      // V55.7: Cloud Function 호출
      const deleteEmployeePhoto = httpsCallable<
        { employeeId: string },
        { success: boolean }
      >(functions, 'deleteEmployeePhoto');

      const result = await deleteEmployeePhoto({ employeeId });

      if (result.data.success) {
        // V55.11: 즉시 UI 반영 - 로컬 상태 초기화
        setLocalPhotoPath(undefined);
        setResolvedURL(undefined);
        setImageError(false); // 에러 상태 초기화
        alert('사진이 삭제되었습니다.');
        onRefresh(); // 서버 데이터 동기화
      } else {
        throw new Error('삭제 실패');
      }
    } catch (error: any) {
      console.error('[EmployeePhotoSection] 삭제 실패:', error instanceof Error ? error.message : String(error));

      // Firebase Functions 에러 메시지 추출
      const errorMessage = error.message || '사진 삭제에 실패했습니다.';
      alert(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // V55.11: 로딩 상태 포함
  const isProcessing = isUploading || isDeleting || isLoadingURL;

  return (
    <div style={containerStyle}>
      {/* 사진 영역 (3:4 비율 고정) */}
      <div style={photoContainerStyle}>
        {resolvedURL && !imageError ? (
          <img
            src={displayPhotoURL}
            alt={`${employeeName} 프로필 사진`}
            style={photoStyle}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        ) : (
          <div style={placeholderStyle}>
            <User size={48} color="var(--color-gray-400)" />
          </div>
        )}

        {/* V55.11: 이미지 로딩 실패 메시지 */}
        {imageError && localPhotoPath && (
          <div style={errorOverlayStyle}>
            <AlertCircle size={16} color="var(--color-error)" />
            <span style={errorTextStyle}>사진 로드 실패</span>
          </div>
        )}

        {/* 로딩 오버레이 */}
        {isProcessing && (
          <div style={loadingOverlayStyle}>
            <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
      </div>

      {/* 버튼 영역 */}
      {canEdit && (
        <div style={buttonContainerStyle}>
          <button
            onClick={handleAddClick}
            disabled={isProcessing}
            style={addButtonStyle}
          >
            <Camera size={14} />
            {localPhotoPath ? '사진 변경' : '사진 추가'}
          </button>

          {localPhotoPath && (
            <button
              onClick={handleDeleteClick}
              disabled={isProcessing}
              style={deleteButtonStyle}
            >
              <Trash2 size={14} />
              삭제
            </button>
          )}
        </div>
      )}

      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>사진 삭제</h4>
            <p style={{ margin: '0 0 20px 0', color: 'var(--color-gray-600)' }}>
              직원 사진을 삭제하시겠습니까?
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={handleDeleteCancel} style={cancelButtonStyle}>
                취소
              </button>
              <button onClick={handleDeleteConfirm} style={confirmDeleteButtonStyle}>
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
};

const photoContainerStyle: React.CSSProperties = {
  position: 'relative',
  width: PHOTO_WIDTH,
  height: PHOTO_HEIGHT,
  borderRadius: '8px',
  overflow: 'hidden',
  border: '2px solid var(--color-gray-200)',
  background: 'var(--color-gray-100)',
};

const photoStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover', // 3:4 비율로 crop, 찌그러짐 없음
  objectPosition: 'center top', // 얼굴이 주로 상단에 있으므로 top 우선
};

const placeholderStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--color-gray-100)',
};

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.8)',
};

const buttonContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const addButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: '500',
  background: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const deleteButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: '500',
  background: 'white',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: '6px',
  cursor: 'pointer',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle: React.CSSProperties = {
  background: 'white',
  padding: '24px',
  borderRadius: '12px',
  maxWidth: '360px',
  width: '90%',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: '14px',
  background: 'var(--color-gray-100)',
  color: 'var(--color-gray-700)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const confirmDeleteButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: '14px',
  background: 'var(--color-error)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

// V55.10: 이미지 로딩 실패 오버레이
const errorOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '4px',
  background: 'rgba(255, 255, 255, 0.9)',
  borderTop: '1px solid var(--color-error)',
};

const errorTextStyle: React.CSSProperties = {
  fontSize: '10px',
  color: 'var(--color-error)',
};
